import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ApiError,
  detachDocument,
  documentFileUrl,
  listAttachments,
  uploadDocument,
  type Attachment,
} from '../api.js';
import { formatSize } from './Documents.js';

/**
 * Files on one record — a photo on a snag, a certificate on an NCR closure.
 *
 * ONE COMPONENT, SIX MODULES
 * --------------------------
 * The diary, drawing revisions, snags, NCR completions, checklist responses and
 * submittals all need the same thing: attach a file, see what is attached, take
 * one off. Six copies of that would be six places to fix the next bug in it, so
 * this is written once and given an `entityType` — matching the polymorphic
 * `attachment` table underneath, which exists for the same reason.
 *
 * UPLOADING AND ATTACHING ARE ONE ACTION HERE, AND ONE TRANSACTION UNDERNEATH
 * ---------------------------------------------------------------------------
 * Somebody photographing a snag on site does not want to upload a file, find
 * the register, and then attach it. So the button does both — and since S1.2 it
 * does both in ONE call, which the server commits as one transaction. Before
 * that it was two calls, and the gap between them was a real state: the bytes
 * saved, the link missing, and a file in the register attached to nothing.
 *
 * `attachDocument` still exists and still takes a document id rather than
 * bytes, because a drawing already in the project should be attachable without
 * being uploaded twice. That path is the register's, and it is a different act.
 */

interface Props {
  readonly projectId: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly can: { readonly write: boolean };
  /** What the files are, for the empty state. "photos", "certificates". */
  readonly noun?: string;
  /** Tick this on upload so the client and consultant can see it. */
  readonly shareByDefault?: boolean;
  readonly category?: string;
}

export function Attachments({
  projectId,
  entityType,
  entityId,
  can,
  noun = 'files',
  shareByDefault = false,
  category = 'photo',
}: Props): JSX.Element {
  const [items, setItems] = useState<readonly Attachment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const inputId = `attach-${entityType}-${entityId}`;

  const load = useCallback(async () => {
    try {
      setItems(await listAttachments(projectId, entityType, entityId));
    } catch {
      // A denied or missing attachment list is not worth an error banner on a
      // record whose own screen has already said what the reader may see.
      setItems([]);
    }
  }, [projectId, entityType, entityId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onFile(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // ONE CALL, ONE TRANSACTION. The subject rides on the upload and the
      // server writes the document, its version and the link together or not at
      // all. This used to be an upload followed by an attach, which could
      // half-succeed and leave a file in the register attached to nothing —
      // exactly the untagged pile the per-record flow exists to prevent.
      await uploadDocument(projectId, file, {
        category,
        external: shareByDefault,
        attachTo: { entityType, entityId },
      });
      // Re-read rather than trusting a returned list. It is one extra READ, and
      // a read cannot leave anything half-written.
      await load();
    } catch (caught) {
      // "Not saved", not "did not attach". After this failure nothing was
      // written at all, and sending somebody to look for the file in the
      // register would send them after a file that is not there.
      setError(caught instanceof ApiError ? caught.message : 'That file was not saved.');
    } finally {
      setBusy(false);
    }
  }

  if (!items) return <p className="hint">Loading {noun}…</p>;

  return (
    <div className="attachments">
      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {items.length > 0 && (
        <ul className="attachment-list">
          {items.map((item) => (
            <li key={item.id}>
              {/*
                A file the server will not serve is NAMED but not linked. Same
                rule as the files register: an action its reader cannot take
                does not belong on the screen, and the reason is said in words
                rather than left as a link that fails when clicked.
              */}
              {item.can_download ? (
                <a href={documentFileUrl(projectId, item.document_id)} download>
                  {item.filename}
                </a>
              ) : (
                <span>{item.filename}</span>
              )}
              <span className="hint">
                {' '}
                {formatSize(item.size_bytes)}
                {item.is_external_visible ? ' · shared' : ' · internal'}
                {item.attached_by_name ? ` · ${item.attached_by_name}` : ''}
                {!item.can_download &&
                  (item.scan_status === 'infected'
                    ? ' · malware found, cannot be downloaded'
                    : ' · not checked for malware yet, cannot be downloaded')}
              </span>
              {can.write && (
                <button
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void detachDocument(projectId, item.id)
                      .then(load)
                      .catch(() => setError('That did not come off.'))
                      .finally(() => setBusy(false));
                  }}
                >
                  Remove
                  <span className="visually-hidden"> {item.filename}</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {can.write && (
        <>
          <label htmlFor={inputId} className="link-button file-button">
            {busy ? 'Attaching…' : items.length === 0 ? `Attach ${noun}` : 'Attach another'}
          </label>
          <input
            id={inputId}
            ref={input}
            type="file"
            className="visually-hidden"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void onFile(file);
              // Cleared so the same file chosen twice still fires `change`.
              if (input.current) input.current.value = '';
            }}
          />
        </>
      )}

      {!can.write && items.length === 0 && <p className="hint">No {noun}.</p>}
    </div>
  );
}
